<template>
  <div class="video-recorder-container">
    <!-- 头部 -->
    <div class="header">
      <!-- <h2 class="title">{{ title }}</h2> -->
      <div v-if="!isRecording && !previewVideoUrl" class="mode-selector">
        <button
          class="mode-btn"
          :class="{ active: mode === 'record' }"
          @click="mode = 'record'"
        >
          录像
        </button>
        <button
          class="mode-btn"
          :class="{ active: mode === 'upload' }"
          @click="mode = 'upload'"
        >
          上传
        </button>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- 录像模式 -->
    <div v-if="mode === 'record' && !previewVideoUrl">
      <!-- 视频预览区域 -->
      <div class="video-preview-wrapper" :class="{ hidden: !isCameraActive }">
        <video
          ref="videoPreview"
          class="video-preview"
          :class="{ mirrored: isFrontCamera }"
          autoplay
          playsinline
          muted
        ></video>

        <!-- 录制中状态 -->
        <div v-if="isRecording" class="recording-status">
          <div class="recording-indicator">
            <span class="recording-dot"></span>
            <span class="recording-text">录制中</span>
          </div>
          <div class="recording-timer">
            {{ formatTime(recordingTime) }}
          </div>
        </div>
      </div>

      <!-- 摄像头未开启时的占位图 -->
      <div v-if="!isCameraActive" class="camera-placeholder">
        <div class="placeholder-icon">📷</div>
        <p class="placeholder-text">摄像头未开启</p>
        <button class="btn btn-primary" @click="startCamera">
          开启摄像头
        </button>
      </div>

      <!-- 控制按钮 -->
      <div class="controls">
        <template v-if="!isCameraActive">
          <button class="btn btn-primary" @click="startCamera">
            打开录像器
          </button>
        </template>

        <template v-else>
          <!-- 摄像头控制 -->
          <div class="camera-controls">
            <button
              class="btn btn-icon"
              @click="toggleCamera"
              :disabled="isRecording"
              title="切换摄像头"
            >
              <span class="btn-icon">🔄</span>
            </button>
            <button
              class="btn btn-icon"
              @click="toggleFlash"
              :disabled="isRecording || !hasFlash"
              title="闪光灯"
            >
              <span class="btn-icon">{{ flashIcon }}</span>
            </button>
          </div>

          <!-- 录制控制 -->
          <div class="recording-controls">
            <button
              v-if="!isRecording"
              class="btn btn-record"
              @click="startRecording"
              :disabled="!isCameraActive"
            >
              <span class="record-icon"></span>
            </button>

            <button v-else class="btn btn-stop" @click="stopRecording">
              <span class="stop-icon"></span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- 上传模式 -->
    <div v-if="mode === 'upload' && !previewVideoUrl" class="upload-container">
      <div class="upload-area" @click="triggerFileInput">
        <div class="upload-icon">📁</div>
        <p class="upload-text">点击选择视频文件</p>
        <p class="upload-hint">支持 MP4, WebM, MOV 格式</p>
        <input
          ref="fileInput"
          type="file"
          accept="video/*"
          @change="handleFileSelect"
          class="file-input"
        />
      </div>

      <div class="upload-info">
        <p>或者直接录制视频：</p>
        <button class="btn btn-secondary" @click="mode = 'record'">
          切换到录像模式
        </button>
      </div>
    </div>

    <!-- 视频预览 -->
    <div v-if="previewVideoUrl" class="preview-container">
      <h3 class="preview-title">
        {{ isRecording ? "录制完成" : "已选择视频" }}
      </h3>

      <div class="video-wrapper">
        <video
          ref="previewVideo"
          :src="previewVideoUrl"
          controls
          class="preview-video"
          @loadedmetadata="onVideoLoaded"
        ></video>
      </div>

      <div class="video-info">
        <p>时长: {{ formatTime(videoDuration) }}</p>
        <p v-if="videoSize">大小: {{ formatFileSize(videoSize) }}</p>
      </div>

      <div class="preview-controls">
        <button class="btn btn-secondary" @click="cancelPreview">
          取消
        </button>
        <button class="btn btn-primary" @click="confirmVideo">
          确认使用
        </button>
        <button
          v-if="allowDownload"
          class="btn btn-download"
          @click="downloadVideo"
        >
          下载
        </button>
      </div>
    </div>

    <!-- 使用说明 -->
    <div v-if="showInstructions" class="instructions">
      <h4>使用说明：</h4>
      <ul>
        <li>录像模式：点击圆形按钮开始/停止录制</li>
        <li>上传模式：选择手机中的视频文件</li>
        <li>录制时长限制：{{ maxDuration / 1000 }}秒</li>
        <li>支持前后摄像头切换</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoRecorder",

  props: {
    // 组件标题
    title: {
      type: String,
      default: "视频录制",
    },
    // 最大录制时长（毫秒）
    maxDuration: {
      type: Number,
      default: 60000, // 60秒
    },
    // 是否允许下载
    allowDownload: {
      type: Boolean,
      default: true,
    },
    // 是否显示使用说明
    showInstructions: {
      type: Boolean,
      default: true,
    },
    // 默认使用的摄像头
    defaultCamera: {
      type: String,
      default: "environment", // 'environment' 后置, 'user' 前置
      validator: (value) => ["environment", "user"].includes(value),
    },
  },

  data() {
    return {
      mode: "record", // 'record' 或 'upload'
      isCameraActive: false,
      isRecording: false,
      isFrontCamera: this.defaultCamera === "user",
      flashOn: false,
      mediaStream: null,
      mediaRecorder: null,
      recordedChunks: [],
      recordingTime: 0,
      recordingTimer: null,
      previewVideoUrl: null,
      videoDuration: 0,
      videoSize: 0,
      errorMessage: "",
      hasFlash: false,
      constraints: {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: this.defaultCamera,
          frameRate: { ideal: 30 },
        },
        audio: true,
      },
    };
  },

  computed: {
    flashIcon() {
      return this.flashOn ? "💡" : "🔅";
    },
  },

  mounted() {
    this.checkCompatibility();
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    // 检查浏览器兼容性
    checkCompatibility() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorMessage = "您的浏览器不支持摄像头功能";
        return false;
      }

      if (!window.MediaRecorder) {
        this.errorMessage = "您的浏览器不支持视频录制";
        return false;
      }

      return true;
    },

    // 开启摄像头
    async startCamera() {
      if (!this.checkCompatibility()) return;

      try {
        // 停止之前的流
        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach((track) => track.stop());
        }

        // 设置摄像头方向
        this.constraints.video.facingMode = this.isFrontCamera
          ? "user"
          : "environment";

        // 获取摄像头权限
        this.mediaStream = await navigator.mediaDevices.getUserMedia(
          this.constraints
        );

        // 设置视频预览
        if (this.$refs.videoPreview) {
          this.$refs.videoPreview.srcObject = this.mediaStream;
        }

        this.isCameraActive = true;
        this.errorMessage = "";

        // 检查是否支持闪光灯
        this.checkFlashSupport();

        this.$emit("camera-started");
      } catch (error) {
        console.error("开启摄像头失败:", error);
        this.errorMessage = `无法访问摄像头: ${error.message}`;
        this.$emit("error", error);
      }
    },

    // 检查闪光灯支持
    checkFlashSupport() {
      // 简单检查，实际实现可能需要更复杂
      const videoTrack = this.mediaStream.getVideoTracks()[0];
      if (videoTrack && "applyConstraints" in videoTrack) {
        // 这里可以尝试应用闪光灯约束
        this.hasFlash = false; // 简化处理，实际需要设备检测
      } else {
        this.hasFlash = false;
      }
    },

    // 切换摄像头
    toggleCamera() {
      if (this.isRecording) return;

      this.isFrontCamera = !this.isFrontCamera;
      this.startCamera();
    },

    // 切换闪光灯
    toggleFlash() {
      if (!this.hasFlash || this.isRecording) return;

      // 这里需要根据具体设备实现闪光灯控制
      // 通常通过调整视频约束实现
      this.flashOn = !this.flashOn;
      console.log("闪光灯状态:", this.flashOn ? "开启" : "关闭");
    },

    // 开始录制
    startRecording() {
      if (!this.mediaStream || this.isRecording) return;

      this.recordedChunks = [];
      this.recordingTime = 0;

      // 设置MIME类型
      const mimeTypes = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/mp4",
      ];

      let selectedMimeType = "";
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        this.errorMessage = "您的浏览器不支持视频录制";
        return;
      }

      try {
        const options = {
          mimeType: selectedMimeType,
          videoBitsPerSecond: 2500000,
          audioBitsPerSecond: 128000,
        };

        this.mediaRecorder = new MediaRecorder(this.mediaStream, options);

        // 收集数据
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        // 录制完成
        this.mediaRecorder.onstop = () => {
          if (this.recordedChunks.length === 0) {
            console.warn("没有录制数据");
            return;
          }

          const blob = new Blob(this.recordedChunks, {
            type: this.mediaRecorder.mimeType,
          });

          this.previewVideoUrl = URL.createObjectURL(blob);
          this.videoSize = blob.size;
          this.isRecording = false;

          // 停止计时器
          clearInterval(this.recordingTimer);
          this.recordingTimer = null;

          // 停止摄像头流（可选，根据需求）
          // this.stopCamera()

          this.$emit("recording-complete", {
            url: this.previewVideoUrl,
            blob: blob,
            duration: this.recordingTime,
            size: blob.size,
          });
        };

        // 开始录制
        this.mediaRecorder.start(1000); // 每1秒收集一次数据
        this.isRecording = true;

        // 开始计时
        this.recordingTimer = setInterval(() => {
          this.recordingTime += 1;

          // 检查是否超过最大时长
          if (this.recordingTime >= this.maxDuration / 1000) {
            this.stopRecording();
          }
        }, 1000);

        this.$emit("recording-started");
      } catch (error) {
        console.error("开始录制失败:", error);
        this.errorMessage = `录制失败: ${error.message}`;
        this.$emit("error", error);
      }
    },

    // 停止录制
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
      }
    },

    // 停止摄像头
    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }

      if (this.$refs.videoPreview) {
        this.$refs.videoPreview.srcObject = null;
      }

      this.isCameraActive = false;
      this.$emit("camera-stopped");
    },

    // 触发文件选择
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // 处理文件选择
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 验证文件类型
      if (!file.type.startsWith("video/")) {
        this.errorMessage = "请选择视频文件";
        return;
      }

      // 验证文件大小（可选，例如限制100MB）
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        this.errorMessage = "文件大小不能超过100MB";
        return;
      }

      this.previewVideoUrl = URL.createObjectURL(file);
      this.videoSize = file.size;

      this.$emit("file-selected", {
        file: file,
        url: this.previewVideoUrl,
        size: file.size,
      });
    },

    // 视频加载完成
    onVideoLoaded() {
      const video = this.$refs.previewVideo;
      if (video) {
        this.videoDuration = Math.round(video.duration);
      }
    },

    // 取消预览
    cancelPreview() {
      // 释放URL对象
      if (this.previewVideoUrl) {
        URL.revokeObjectURL(this.previewVideoUrl);
      }

      this.previewVideoUrl = null;
      this.videoDuration = 0;
      this.videoSize = 0;

      // 重置文件输入
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }

      this.$emit("preview-cancelled");
    },

    // 确认使用视频
    confirmVideo() {
      if (!this.previewVideoUrl) return;

      const videoData = {
        url: this.previewVideoUrl,
        duration: this.videoDuration,
        size: this.videoSize,
        mode: this.mode,
      };

      this.$emit("video-confirmed", videoData);

      // 注意：不在这里释放URL，因为父组件可能需要使用
    },

    // 下载视频
    downloadVideo() {
      if (!this.previewVideoUrl) return;

      const link = document.createElement("a");
      link.href = this.previewVideoUrl;

      const timestamp = new Date().getTime();
      const extension = this.mediaRecorder?.mimeType?.includes("mp4")
        ? "mp4"
        : "webm";
      link.download = `video_${timestamp}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$emit("video-downloaded");
    },

    // 格式化时间（秒 -> MM:SS）
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    // 清理资源
    cleanup() {
      // 停止录制
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
      }

      // 停止摄像头
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      }

      // 清除计时器
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
      }

      // 释放URL对象
      if (this.previewVideoUrl) {
        URL.revokeObjectURL(this.previewVideoUrl);
      }
    },
  },
};
</script>

<style scoped>
.video-recorder-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.mode-selector {
  display: flex;
  gap: 8px;
}

.mode-btn {
  padding: 6px 16px;
  border: 2px solid #007bff;
  background: transparent;
  color: #007bff;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.mode-btn.active {
  background: #007bff;
  color: white;
}

.error-message {
  padding: 12px;
  margin-bottom: 16px;
  background: #f8d7da;
  color: #721c24;
  border-radius: 8px;
  text-align: center;
}

.video-preview-wrapper {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 75%; /* 4:3 比例，适合手机 */
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.video-preview-wrapper.hidden {
  display: none;
}

.video-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview.mirrored {
  transform: scaleX(-1);
}

.recording-status {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  border-radius: 20px;
  color: white;
}

.recording-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recording-dot {
  width: 12px;
  height: 12px;
  background: #ff4757;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.recording-timer {
  font-weight: bold;
  font-size: 1.1rem;
}

.camera-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #e9ecef;
  border-radius: 8px;
  margin-bottom: 20px;
}

.placeholder-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.placeholder-text {
  margin: 0 0 20px 0;
  color: #666;
}

.controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.camera-controls {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-icon {
  width: 50px;
  height: 50px;
  padding: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-record,
.btn-stop {
  width: 70px;
  height: 70px;
  padding: 0;
  border-radius: 50%;
  border: none;
  cursor: pointer;
}

.btn-record {
  background: #ff4757;
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.4);
}

.btn-stop {
  background: #747d8c;
  box-shadow: 0 4px 15px rgba(116, 125, 140, 0.4);
}

.record-icon,
.stop-icon {
  display: block;
  width: 30px;
  height: 30px;
  margin: 0 auto;
}

.record-icon {
  background: white;
  border-radius: 4px;
}

.stop-icon {
  background: white;
}

.upload-container {
  margin-top: 20px;
}

.upload-area {
  position: relative;
  padding: 60px 20px;
  background: #e9ecef;
  border: 2px dashed #6c757d;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-area:hover {
  background: #dee2e6;
  border-color: #495057;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.upload-text {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.upload-hint {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.file-input {
  display: none;
}

.upload-info {
  margin-top: 20px;
  text-align: center;
}

.preview-container {
  margin-top: 20px;
  animation: fadeIn 0.5s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.preview-title {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.video-wrapper {
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
}

.preview-video {
  width: 100%;
  height: auto;
  display: block;
}

.video-info {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding: 12px;
  background: #e9ecef;
  border-radius: 8px;
  font-size: 14px;
}

.preview-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-download {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.instructions {
  margin-top: 30px;
  padding: 16px;
  background: #e9ecef;
  border-radius: 8px;
  font-size: 14px;
}

.instructions h4 {
  margin-top: 0;
  margin-bottom: 12px;
}

.instructions ul {
  margin: 0;
  padding-left: 20px;
  color: #666;
}

.instructions li {
  margin-bottom: 8px;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .video-recorder-container {
    padding: 12px;
  }

  .title {
    font-size: 1.3rem;
  }

  .btn {
    padding: 10px 20px;
    font-size: 15px;
  }

  .btn-record,
  .btn-stop {
    width: 60px;
    height: 60px;
  }

  .record-icon,
  .stop-icon {
    width: 24px;
    height: 24px;
  }
}

@media (max-height: 700px) {
  .video-preview-wrapper {
    padding-bottom: 60%; /* 调整高度比例 */
  }
}
</style>
